import { useState } from 'react';
import { INK_MUTED, TooltipBox, useElementWidth } from './common';

/**
 * Mapa de calor 7 × 24 (dia da semana × hora local do início da recarga).
 * Sequencial de UMA cor (azul Bow, claro → escuro) por kWh. Célula vazia é a
 * superfície com contorno sutil — "zero" é dado, não buraco.
 */
export function Heatmap({
  dias,
  grid,
  formatKwh
}: {
  dias: string[];
  grid: { recargas: number; kwh: number }[][];
  formatKwh: (v: number) => string;
}) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<[number, number] | null>(null);
  const vmax = Math.max(0, ...grid.flat().map((c) => c.kwh));
  const padL = 30;
  const gap = 2;
  const cell = Math.min(Math.max(Math.floor((width - padL - gap * 23) / 24), 8), 26);
  const h = cell * 7 + gap * 6 + 18;
  const fill = (k: number) =>
    k <= 0 || vmax <= 0 ? '#F4F2EC' : `rgba(0,144,226,${(0.15 + 0.85 * (k / vmax)).toFixed(3)})`;

  return (
    <div ref={ref} className="relative">
      {hover && (
        <TooltipBox
          width={width}
          x={padL + hover[1] * (cell + gap)}
          title={`${dias[hover[0]]} · ${hover[1]}h`}
          rows={[
            { label: 'kWh', value: formatKwh(grid[hover[0]]![hover[1]]!.kwh) },
            { label: 'recargas', value: String(grid[hover[0]]![hover[1]]!.recargas) }
          ]}
        />
      )}
      <svg width={width} height={h} role="img" aria-label="Recargas por dia da semana e hora" style={{ display: 'block' }}>
        {grid.map((row, d) => (
          <g key={dias[d]}>
            <text x={0} y={d * (cell + gap) + cell * 0.72} fontSize={10} fill={INK_MUTED}>
              {dias[d]}
            </text>
            {row.map((c, hr) => (
              <rect
                key={hr}
                x={padL + hr * (cell + gap)}
                y={d * (cell + gap)}
                width={cell}
                height={cell}
                rx={3}
                fill={fill(c.kwh)}
                tabIndex={0}
                aria-label={`${dias[d]} ${hr}h: ${c.recargas} recargas, ${formatKwh(c.kwh)}`}
                onPointerEnter={() => setHover([d, hr])}
                onPointerDown={() => setHover([d, hr])}
                onFocus={() => setHover([d, hr])}
                onPointerLeave={() => setHover(null)}
                onBlur={() => setHover(null)}
                style={{ outline: 'none' }}
                stroke={hover && hover[0] === d && hover[1] === hr ? '#001B74' : 'none'}
              />
            ))}
          </g>
        ))}
        {[0, 6, 12, 18, 23].map((hr) => (
          <text
            key={hr}
            x={padL + hr * (cell + gap) + cell / 2}
            y={h - 4}
            textAnchor="middle"
            fontSize={10}
            fill={INK_MUTED}
          >
            {hr}h
          </text>
        ))}
      </svg>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-bow-royal/55">
        menos
        <span
          aria-hidden
          className="h-2 w-24 rounded-full"
          style={{ background: 'linear-gradient(90deg, rgba(0,144,226,0.15), rgba(0,144,226,1))' }}
        />
        mais kWh
      </div>
    </div>
  );
}
